import { CreateTaskEventResponse, ResolveTaskRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useTasksTranslation } from '../hooks';
import {
    addOrUpdateAllTasksCache,
    addOrUpdateTaskEventsCache
} from '../queries/tasks-cache';

type UseResolveTaskActionProps = {
    taskId: string;
};

type UseResolveTaskActionData = {
    resolve: (data: ResolveTaskRequest) => Promise<CreateTaskEventResponse>;
    loading: boolean;
    error: string;
};

/**
 * Closing an alert out: whether it was a false alarm, and the reasons for it.
 *
 * No `isAllowed`/`reason`: whether a viewer may resolve is decided a screen up
 * by `useTaskPermissions(task)` — it needs the whole task to answer, not just
 * its id — and reaches the button as `canResolve`, together with the alert's
 * own state.
 */
export function useResolveTaskAction(
    props: UseResolveTaskActionProps
): UseResolveTaskActionData {
    const { taskId } = props;

    const { t } = useTasksTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. `POST /v3/tasks/{taskId}/resolve`
    // declares `BAD_REQUEST`, `TASK_NOT_ALERTED`, `TASK_NOT_FOUND` and
    // `USER_NOT_FOUND`; anything else is a transport or auth failure, which
    // `getFallbackErrorKey` names. `TASK_NOT_ALERTED` reads differently here
    // than it does on assign, so it is this action's own key.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return (
                    error.response?.data.message.join(
                        t('shared.tasks.list_separator')
                    ) ?? ''
                );
            }
            if (isApiError(error, 'TASK_NOT_ALERTED')) {
                return t('actions.resolve_alert.error_task_not_alerted');
            }
            if (isApiError(error, 'TASK_NOT_FOUND')) {
                return t('shared.errors.task_not_found');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: ResolveTaskRequest) =>
            openApiRef.current.tasks
                .resolve(taskId, variables)
                .then((response) => response.data),
        onSuccess: (response) => {
            // Write the server's answer into the cache — never invalidate and
            // refetch what the response already handed you.
            const { task, events } = response.data;
            addOrUpdateAllTasksCache(queryClient, task);
            addOrUpdateTaskEventsCache(queryClient, taskId, events);

            UIToast.success(t('actions.resolve_alert.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        resolve: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
